import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Securitee > GravityZone (`SecuriteeGravityZoneController`): statiile citite
 * din GravityZone de cronul ph4 `securitee/gravityzone`, pentru firmele legate
 * in Settings > Securitee > GravityZone.
 */

export type GzStatusKind = 'no_agent' | 'malware' | 'issues' | 'not_seen' | 'protected' | 'pending'

export type GzStatus = { status: GzStatusKind; label: string; reasons: string[] }

export type GzLinkInfo = {
  company_id: number
  company_name: string
  gz_company_name: string
  synced_at: string | null
  sync_error: string | null
  endpoints: number | null
  matched: number | null
}

export type GzEndpointRow = GzStatus & {
  gz_endpoint_id: string
  name: string
  device_id: number | null
  device_name: string | null
  company_name: string
  policy_name: string | null
  last_seen_at: string | null
  risk_score: number | null
  /** cum s-a gasit aparatul: dupa MAC, dupa nume, sau de mana ("Match to device") */
  match_method?: 'mac' | 'name' | 'manual' | null
  /** ce numara auditul: firewall-ul GZ oprit, Power User pornit; null = necunoscut / fara agent GZ */
  firewall_off: boolean | null
  power_user_on: boolean | null
}

export type GzOverview = {
  linked: boolean
  /** Legata, dar necitita inca din GravityZone. */
  waiting: boolean
  /** poate potrivi de mana statii GZ cu aparate OD (editare pe All devices) */
  canMatch?: boolean
  links: GzLinkInfo[]
  cards: {
    od_devices: number
    with_agent: number
    without_agent: number
    malware: number
    issues: number
    not_seen: number
    modules_off: { key: string; label: string; count: number }[]
    policy_not_applied: number
    signatures_outdated: number
    /** `of` = statiile cu agent GZ; nu schimba starea */
    hardening: {
      firewall_off: number
      power_user_on: number
      of: number
      /** pe politica, cum numara auditul: `of` = politicile citite care au statii */
      policies?: { of: number; firewall_off: number; power_user_on: number; ransomware_off: number }
    }
  }
  /** `in_gz`: GZ stie aparatul, dar fara agent; altfel GZ nu-l stie deloc */
  in_od_not_gz: { device_id: number; name: string; company_name: string; last_activity: string | null; in_gz?: boolean }[]
  in_gz_not_od: { gz_endpoint_id: string; name: string; company_name: string; os: string | null; is_managed: boolean; last_seen_at: string | null }[]
  endpoints: GzEndpointRow[]
  /** lipsesc la un API mai vechi */
  policies?: GzPolicy[]
}

/** O politica GZ a firmei; setarile null = necitite. */
export type GzPolicy = { id: string; name: string; endpoints: number; firewall: boolean | null; power_user: boolean | null; ransomware: boolean | null }

export type GzModule = { key: string; label: string; on: boolean; essential: boolean }

/** Un eveniment GravityZone primit prin push, pentru statia aparatului (ultimele 30 de zile). */
export type GzEvent = { at: string; type: string; detail: string; severity: 'danger' | 'warning' | 'info' }

export type GzDevice = {
  linked: boolean
  status: GzStatus | null
  endpoint: null | {
    name: string
    fqdn: string | null
    macs: string[]
    match_method: 'mac' | 'name' | 'manual' | null
    os: string | null
    policy_name: string | null
    policy_applied: boolean | null
    agent_version: string | null
    signatures_at: string | null
    signatures_outdated: boolean | null
    last_seen_at: string | null
    online: boolean | null
    malware_infected: boolean | null
    synced_at: string
    risk: { score: number | null; impact: string | null; misconfig: number | null; app_vuln: number | null; human: number | null }
  }
  modules: GzModule[]
  /** lipseste la un API mai vechi */
  events?: GzEvent[]
}

/** Push-ul conexiunii firmei: ok / quiet (tacut, neverificat) / checking (proba plecata) / silent (proba n-a ajuns). */
export type GzPushHealth = { state: 'off' | 'ok' | 'quiet' | 'checking' | 'silent'; silent_minutes: number | null }

/** Ce a facut deschiderea ecranului: citirea (daca datele erau vechi) si proba push-ului tacut. */
export type GzRefresh = { sync: 'started' | 'fresh' | 'failed' | 'not_configured' | 'not_linked'; push: GzPushHealth | null }

/** "Match to device": statia GZ legata de mana de un aparat OD al firmei; intoarce ecranul. */
export function gzMatch(gzEndpointId: string, deviceId: number) {
  return apiPost<GzOverview>('/securitee/gravityzone/matches', { gz_endpoint_id: gzEndpointId, device_id: deviceId })
}

export function gzUnmatch(gzEndpointId: string) {
  return apiPost<GzOverview>('/securitee/gravityzone/matches/unmatch', { gz_endpoint_id: gzEndpointId })
}

/** Fara cron: ecranul il cheama cand se deschide (si cat se verifica proba). */
export function gzRefresh() {
  return apiPost<GzRefresh>('/securitee/gravityzone/refresh', {})
}

export function gzOverviewQuery() {
  return queryOptions({
    queryKey: ['securitee', 'gravityzone'],
    queryFn: () => apiGet<GzOverview>('/securitee/gravityzone'),
  })
}

export function gzDeviceQuery(deviceId: number) {
  return queryOptions({
    queryKey: ['securitee', 'gravityzone', 'device', deviceId],
    queryFn: () => apiGet<GzDevice>(`/securitee/devices/${deviceId}/gravityzone`),
  })
}
